// libraries
import { useCallback, useEffect, useState, useMemo } from 'react'
import { startCase, toLower } from 'lodash'
import Dinero from 'dinero.js'
import Big from 'big.js'
import { Link } from 'react-router-dom'
import { Box, Typography, Stack, Tooltip, IconButton } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { styled } from '@mui/system'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { RemoveRedEye as RemoveRedEyeIcon, Edit as EditIcon } from '@mui/icons-material'

// components
import BasicDataGrid from 'components/generic/datagrid'
import EditableCell from 'components/generic/datagrid/cells/EditableCell'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import { TransparentIconLabelButton } from 'components/generic/buttons/IconLabelButton'

// queries and mutations
import { useGetPriceSheetQuery } from 'services/priceSheets'
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// utils and helpers
import {
  setDecimalPrecision,
  formatNumber,
  convertUM,
  checkIfDateIsLocked,
  generateLockTooltip,
  formatNumberForDisplay,
} from 'utils/helpers'
import {
  calculateTotalWeight,
  getTotalWeightDeduction,
  getTotalPriceDeduction,
  generateAdjustmentText,
  generateAdjustmentTextFromMaterial,
  getNetValue,
} from 'utils/data-manipulation/common'
import { INVOICE_WEIGHT_MODES, MEASURE_UNIT_ARRAY, FEE_OR_PROMOTION_TYPE, TICKET_SOURCE_TYPE } from 'utils/constants'
import { calculateRowNetValue } from 'components/containers/outbound-tickets/utils'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { roundTo, formatMaterialName } from 'utils/data-manipulation/valueFormatter'
import { ROUNDING_PRECISIONS } from 'utils/constants'
import { InvoiceResponseV3 } from 'gen/rtk-openapi'

const GridStyling = styled('div')(({ theme }) => ({
  '& .MuiDataGrid-root': {
    border: 'none',
    // remove header padding to align headers with column values
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: theme.spacing(0),
    },
    '& .MuiDataGrid-columnHeader': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-cell': {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    '& .MuiDataGrid-columnSeparator': {
      visibility: 'hidden',
    },
    // replace default hover / active-row styling from datagrid
    '& .MuiDataGrid-row': {
      '&.total-row': {
        backgroundColor: theme.palette.info.lightBg,
        fontWeight: '500',
        '&:hover': {
          backgroundColor: theme.palette.info.lightBg,
        },
      },
      '&:not(.total-row)': {
        backgroundColor: 'inherit',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiCircularProgress-root': {
      zIndex: 1,
    },
  },
}))

interface AddToMaterialsDictProps {
  materialTypeId: string
  orderEntryId?: string
  gross: number
  tare: number
  net: number
  itemCount?: number
  isLoose: boolean
  materialName: string
  materialId?: string
}

interface MaterialRow {
  consumerMaterialName: string | null
  consumerGross: number
  consumerTare: number
  consumerPrice: number
  consumerUm: string
  consumerItemCount: number | null

  materialName: string
  yardGross: number
  yardTare: number
  yardNet: number
  yardPrice: number
  yardUm: string
  yardItemCount: number | null

  id: string
  materialTypeId: string
  orderEntryId: string
  materialId?: string // For inbound tickets, stores the original material ID

  allMaterialsNetArray: number[]
  looseMaterialsNetArray: (number | null)[]
  deductions: any[]
  value?: number // For non-material rows (freight, fees, promotions)
}

interface InvoiceTicketTableV2Props {
  ticketInfo: any
  ticketType?: TICKET_SOURCE_TYPE
  updateTotalRowData: any
  invoiceData: InvoiceResponseV3 | undefined
  salesOrderData: any
  purchaseOrderData?: any
  setTicketsAreLoading: any
  weightMode: string
  isEditMode: boolean
  control: any
  setValue: any
  pricePrepend: string
  setCachedMaterialTypesData: any
  apiRefDict: any
  setApiRefDict: any
  setOpenDeduction
  setSelectedDeductions
  setOnSaveDeductionHandler
}

// Helper functions for ticket type checks
const isInboundTicket = (ticketType: TICKET_SOURCE_TYPE) => ticketType === TICKET_SOURCE_TYPE.INBOUND
const isOutboundTicket = (ticketType: TICKET_SOURCE_TYPE) => ticketType === TICKET_SOURCE_TYPE.OUTBOUND

// Helper function to get the appropriate weight/price values based on ticket type
const getTicketValues = (row: any, ticketType: TICKET_SOURCE_TYPE, field: 'gross' | 'tare' | 'price' | 'um') => {
  const prefix = isInboundTicket(ticketType) ? 'yard' : 'consumer'
  return row[`${prefix}${field.charAt(0).toUpperCase() + field.slice(1)}`]
}

const InvoiceTicketTableV2: React.FC<InvoiceTicketTableV2Props> = ({
  ticketInfo,
  ticketType = TICKET_SOURCE_TYPE.OUTBOUND,
  updateTotalRowData,
  invoiceData,
  salesOrderData,
  purchaseOrderData,
  setTicketsAreLoading,
  weightMode,
  isEditMode = false,
  control,
  setValue = () => {},
  pricePrepend = '$',
  setCachedMaterialTypesData,
  apiRefDict = {},
  setApiRefDict = () => {},
  setOpenDeduction,
  setSelectedDeductions,
  setOnSaveDeductionHandler,
}) => {
  const apiRef = useGridApiRef()
  const { defaultYardUm, settings } = useAppSelector(state => state.settings)

  const { decimalPrecision, disableUmDropdowns } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}

  /** Calculates row net weight taking in account the deductions */
  const calculateRowNetWeight = row => {
    const { consumerGross, consumerTare, deductions } = row
    const originalNet = consumerGross - consumerTare
    const weightDeduction = getTotalWeightDeduction(deductions, originalNet)
    return originalNet - weightDeduction
  }

  const openDeductionModal = (id, deductions) => {
    setOpenDeduction(true)
    setOnSaveDeductionHandler(() => deductions => {
      setOpenDeduction(false)

      apiRef.current.updateRows([
        {
          id: id,
          deductions,
        },
      ])
      setValue(`consumerWeights.${ticketInfo.id}.${id}.deductions`, deductions)
      createTotalRow()
    })

    setSelectedDeductions(apiRef.current.getRow(id)!.deductions)
  }

  const { enablePullApartFeatures, enableLockAccountingV2, enableTransferTicketsConsumerWeightsFF } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!ticketInfo || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, ticketInfo.effectiveDate)
  }, [lockAccountingPeriod, ticketInfo, enableLockAccountingV2])

  const { data: priceSheetData, isFetching: isFetchingPriceSheet } = useGetPriceSheetQuery(
    { priceSheetId: salesOrderData?.priceSheetId },
    { skip: !salesOrderData?.priceSheetId },
  )

  const materialTypeIds = useMemo(() => {
    if (ticketInfo) {
      const set = new Set()
      if (isInboundTicket(ticketType)) {
        // For inbound tickets, get material type IDs from materials array
        ticketInfo.materials?.forEach(material => {
          set.add(material.materialTypeId)
        })
      } else {
        // For outbound tickets, get material type IDs from existing structure
        ticketInfo.packingLists?.forEach(packingList => {
          set.add(packingList.finishedGoods?.[0]?.materialTypeId || '')
        })
        ticketInfo.finishedGoods?.forEach(finishedGood => {
          set.add(finishedGood.materialTypeId)
        })
        ticketInfo.materialWeights?.forEach(material => {
          set.add(material.materialTypeId)
        })
      }
      return Array.from(set)
    }
    return []
  }, [ticketInfo, ticketType])

  const { data: materialTypesData, isFetching: isFetchingMaterialTypes } = useGetMaterialTypesQuery(
    { filter: JSON.stringify({ materialTypeIds }) },
    { skip: materialTypeIds.length === 0 },
  )

  const [materials, setMaterials] = useState<MaterialRow[]>([])
  const isLoading = isFetchingPriceSheet || isFetchingMaterialTypes

  useEffect(() => {
    setTicketsAreLoading(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (!ticketInfo.id) return

    if (!apiRefDict[ticketInfo.id]) {
      setApiRefDict(prev => ({
        ...prev,
        [ticketInfo.id]: apiRef,
      }))
    }
  }, [apiRef, ticketInfo.id])

  useEffect(() => {
    if (materialTypesData) {
      setCachedMaterialTypesData(prevState => {
        const newState = { ...prevState }
        materialTypesData?.Items?.forEach(materialType => {
          newState[materialType.id] = materialType
        })
        return newState
      })
    }
  }, [materialTypesData])

  useEffect(() => {
    // Check if this specific ticket exists in the invoice data
    const relevantTicket = invoiceData?.tickets?.find(({ id }) => id === ticketInfo.id)

    if (invoiceData && invoiceData.tickets?.length > 0 && relevantTicket) {
      const formattedMaterials = relevantTicket?.materials.map((material, index) => {
        // For inbound tickets, get adjustments from yardPriceWeight; for outbound, use consumerPriceWeight
        const adjustments = isInboundTicket(ticketType)
          ? material.yardPriceWeight.adjustments ?? []
          : material.consumerPriceWeight?.adjustments ?? []

        // Log weight and price information
        const yardGross = material.yardPriceWeight.grossWeight
        const yardTare = material.yardPriceWeight.tareWeight
        const yardNet = material.yardPriceWeight.netWeight
        const yardPrice = material.yardPriceWeight.pricePerUnit
        const yardUm = material.yardPriceWeight.priceWeightUnit

        // For existing invoice data, ensure unique keys by including index to prevent overwrites
        const key = `${material.materialTypeId}${material.orderEntryId}${index}`
        // Consumer weights may be null, if so read from yard weights
        const formattedMaterial = {
          consumerMaterialName: material.consumerOverrideName,
          consumerGross: material.consumerPriceWeight?.grossWeight ?? material.yardPriceWeight.grossWeight,
          consumerTare: material.consumerPriceWeight?.tareWeight ?? material.yardPriceWeight.tareWeight,
          consumerPrice: material.consumerPriceWeight?.pricePerUnit ?? material.yardPriceWeight.pricePerUnit,
          consumerUm: material.consumerPriceWeight?.priceWeightUnit ?? material.yardPriceWeight.priceWeightUnit,
          deductions: adjustments,

          materialName: `${material.materialName} (${material.materialCode})${material.materialOverrideName ? ' - ' + material.materialOverrideName : ''}`,
          yardGross: material.yardPriceWeight.grossWeight,
          yardTare: material.yardPriceWeight.tareWeight,
          yardNet: material.yardPriceWeight.netWeight,
          allMaterialsNetArray: [material.yardPriceWeight.netWeight],
          looseMaterialsNetArray: [material.yardPriceWeight.looseMaterialNetWeight],
          yardPrice: material.yardPriceWeight.pricePerUnit,
          yardUm: material.yardPriceWeight.priceWeightUnit,

          id: key,
          materialTypeId: material.materialTypeId,
          orderEntryId: material.orderEntryId,

          yardItemCount: material.yardPriceWeight.itemCount,
          consumerItemCount: material.consumerPriceWeight?.itemCount ?? material.yardPriceWeight.itemCount,

          value: (() => {
            // For inbound invoices, use valueBeforeAdjustments to avoid double adjustment application
            if (isInboundTicket(ticketType) || invoiceData?.sourceType === 'INBOUND') {
              return invoiceData.usingConsumerWeights
                ? material.consumerPriceWeight?.valueBeforeAdjustments ??
                    material.yardPriceWeight.valueBeforeAdjustments
                : material.yardPriceWeight.valueBeforeAdjustments
            }

            // For outbound invoices, use total (adjustments handled differently)
            return invoiceData.usingConsumerWeights
              ? material.consumerPriceWeight?.total
              : material.yardPriceWeight.total
          })(),
        }
        setValue(`consumerWeights.${ticketInfo.id}.${key}`, {
          materialTypeId: formattedMaterial.materialTypeId,
          orderEntryId: formattedMaterial.orderEntryId,
          consumerItemCount: formattedMaterial.consumerItemCount,
          consumerGross: formattedMaterial.consumerGross,
          consumerTare: formattedMaterial.consumerTare,
          consumerPrice: formattedMaterial.consumerPrice,
          consumerUm: formattedMaterial.consumerUm,
          consumerMaterialName: formattedMaterial.consumerMaterialName,
          deductions: formattedMaterial.deductions,
        })

        return formattedMaterial
      })

      // Add fees and promotions for inbound invoices
      if (isInboundTicket(ticketType) || invoiceData?.sourceType === 'INBOUND') {
        let feesAndPromotions: any[] = []

        // When viewing an existing inbound invoice, use invoice data
        if (invoiceData && relevantTicket.feesOrPromotions?.length > 0) {
          feesAndPromotions = relevantTicket.feesOrPromotions
        }
        // When creating a new inbound invoice from IBT, use IBT data
        else if (!invoiceData && ticketInfo.feeOrPromotion?.length > 0) {
          feesAndPromotions = ticketInfo.feeOrPromotion
        }

        feesAndPromotions.forEach((feeOrPromotion: any) => {
          const calculatedAmount = feeOrPromotion.calculatedValue ?? (feeOrPromotion.amount || 0)

          let finalValue = calculatedAmount
          if ((feeOrPromotion as any).type === FEE_OR_PROMOTION_TYPE.FEE) {
            finalValue = Math.abs(calculatedAmount)
          } else if ((feeOrPromotion as any).type === FEE_OR_PROMOTION_TYPE.PROMOTION) {
            finalValue = -Math.abs(calculatedAmount)
          }

          formattedMaterials.push({
            id: `fee-${feeOrPromotion.settingId || feeOrPromotion.name?.replace(/\s+/g, '-')}`,
            materialName: feeOrPromotion.name,
            consumerMaterialName: feeOrPromotion.name,
            value: finalValue,
            materialTypeId: '',
            orderEntryId: '',
            yardGross: 0,
            yardTare: 0,
            yardNet: 0,
            consumerGross: 0,
            consumerTare: 0,
            yardItemCount: 0,
            consumerItemCount: 0,
            yardPrice: 0,
            consumerPrice: 0,
            yardUm: defaultYardUm,
            consumerUm: defaultYardUm,
            deductions: [],
            allMaterialsNetArray: [],
            looseMaterialsNetArray: [],
          })
        })
      }

      // Add freight chargeback if present in the invoice data
      if (relevantTicket.freightChargeback) {
        formattedMaterials.push({
          id: 'freight',
          materialName: 'Freight Charge',
          consumerMaterialName: 'Freight Charge',
          materialTypeId: '',
          orderEntryId: '',
          yardGross: 0,
          yardTare: 0,
          yardNet: 0,
          consumerGross: 0,
          consumerTare: 0,
          yardItemCount: 0,
          consumerItemCount: 0,
          yardPrice: 0,
          consumerPrice: 0,
          yardUm: defaultYardUm,
          consumerUm: defaultYardUm,
          value: relevantTicket.freightChargeback,
          deductions: [],
          allMaterialsNetArray: [],
          looseMaterialsNetArray: [],
        })
      }

      setMaterials(formattedMaterials)
    } else if (ticketInfo && materialTypesData && (isInboundTicket(ticketType) || priceSheetData)) {
      const materialsDict = {}
      const addToMaterialsDict = ({
        materialTypeId,
        orderEntryId,
        gross,
        tare,
        net,
        itemCount = 0,
        isLoose,
        materialName,
        materialId = '',
      }: AddToMaterialsDictProps) => {
        // For inbound tickets, always use unique material ID to prevent grouping
        // For outbound tickets, use the original grouping logic
        const key = isInboundTicket(ticketType) && materialId ? materialId : `${materialTypeId}${orderEntryId ?? ''}`

        if (!materialsDict[key]) {
          // note:
          // we are storing allMaterialsNetArray so that row totals can be individually calculated/rounded when using yard weights
          // looseMaterialsNetArray is similar except it is only for loose materials
          // we only use looseMaterialsNetArray for Pull Apart, when the price unit is EACH - see calculateRowNetValue function)
          materialsDict[key] = {
            yardGross: gross,
            yardTare: tare,
            yardNet: net,
            materialTypeId,
            orderEntryId,
            allMaterialsNetArray: [net],
            looseMaterialsNetArray: isLoose ? [net] : [],
            yardItemCount: itemCount,
            materialName,
            materialId, // Store the material ID for lookup
          }
        } else {
          // This should only happen for outbound tickets now
          materialsDict[key].yardGross += gross
          materialsDict[key].yardTare += tare
          materialsDict[key].yardNet += net
          materialsDict[key].allMaterialsNetArray = [...materialsDict[key].allMaterialsNetArray, net]
          materialsDict[key].yardItemCount += itemCount
          if (isLoose) {
            materialsDict[key].looseMaterialsNetArray = [...materialsDict[key].looseMaterialsNetArray, net]
          }
        }
      }

      // Handle inbound ticket materials structure, only build from ticket data when invoice data not available
      if (isInboundTicket(ticketType)) {
        // For inbound tickets, build materials dict from ticket data (fallback when no invoice exists)
        ticketInfo.materials?.forEach(material => {
          const { materialTypeId, orderEntryId, weightAndPrice, materialType, overrideName, id } = material
          if (weightAndPrice) {
            const gross = weightAndPrice.gross?.amount || 0
            const tare = weightAndPrice.tare?.amount || 0
            const net = weightAndPrice.netWeight?.amount || 0
            const itemCount = weightAndPrice.itemCount || 0

            // Get override name from purchase order data if not present on material
            const purchaseOrderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(
              entry => entry.id === orderEntryId,
            )
            const finalOverrideName = overrideName || purchaseOrderEntry?.overrideName || null

            addToMaterialsDict({
              materialTypeId,
              orderEntryId,
              gross,
              tare,
              net,
              itemCount,
              isLoose: true,
              materialName: materialType
                ? formatMaterialName({
                    commonName: materialType.commonName,
                    code: materialType.code,
                    overrideName: finalOverrideName,
                  })
                : '',
              materialId: id,
            })
          }
        })
      } else {
        // Handle outbound ticket materials structure
        ticketInfo.packingLists?.forEach(packingList => {
          const { materialTypeId } = packingList.finishedGoods[0]
          packingList.finishedGoods.forEach(finishedGood => {
            const {
              weight: { gross, tare, net },
              orderEntryId,
              itemCount,
            } = finishedGood
            addToMaterialsDict({
              materialTypeId,
              orderEntryId,
              gross,
              tare,
              net,
              itemCount,
              isLoose: false,
              materialName: '',
            })
          })
        })

        ticketInfo.finishedGoods?.forEach(finishedGood => {
          const {
            weight: { gross, tare, net },
            materialTypeId,
            orderEntryId,
            itemCount,
          } = finishedGood
          addToMaterialsDict({
            materialTypeId,
            orderEntryId,
            gross,
            tare,
            net,
            itemCount,
            isLoose: false,
            materialName: '',
          })
        })

        ticketInfo.materialWeights?.forEach(material => {
          const { materialTypeId, orderEntryId, gross, tare, weight } = material
          addToMaterialsDict({
            materialTypeId,
            orderEntryId,
            gross,
            tare,
            net: weight,
            isLoose: true,
            materialName: '',
          })
        })
      }

      // convert price sheet data into a dict (price, um, and materialName)
      const priceDict = {}
      // Keep track of how many time each material type appears
      const ocurrencesMap = new Map<string, number>()

      if (isInboundTicket(ticketType)) {
        // For inbound tickets, create price dict from weightAndPrice data
        ticketInfo.materials?.forEach(material => {
          const { materialTypeId, orderEntryId, weightAndPrice, materialType, overrideName, id } = material
          if (weightAndPrice) {
            const key = id // Use material ID as key to ensure separate entries
            ocurrencesMap.set(materialTypeId, (ocurrencesMap.get(materialTypeId) ?? 0) + 1)

            // Use Dinero to convert precision-based amounts to proper decimal values
            const rawYardPrice = weightAndPrice.netPrice ? Dinero({ ...weightAndPrice.netPrice }).toUnit() : 0
            const yardPrice = rawYardPrice // Remove frontend sign logic - backend should provide correct values

            // Get override name from purchase order data if not present on material
            const purchaseOrderEntry = purchaseOrderData?.priceSheet?.entriesObj?.find(
              entry => entry.id === orderEntryId,
            )
            const finalOverrideName = overrideName || purchaseOrderEntry?.overrideName || null

            priceDict[key] = {
              yardPrice,
              yardUm: weightAndPrice.um || defaultYardUm,
              materialTypeId: materialTypeId,
              orderEntryId: orderEntryId,
              materialName: materialType
                ? formatMaterialName({
                    commonName: materialType.commonName,
                    code: materialType.code,
                    overrideName: finalOverrideName,
                  })
                : '',
            }
          }
        })
      } else {
        // For outbound tickets, use price sheet data
        priceSheetData.entriesObj?.forEach(priceEntry => {
          const key = `${priceEntry.materialTypeId}${priceEntry.id ?? ''}`
          ocurrencesMap.set(priceEntry.materialTypeId, (ocurrencesMap.get(priceEntry.materialTypeId) ?? 0) + 1)

          priceDict[key] = {
            yardPrice: Dinero({ ...priceEntry?.sellPrice?.price }).toUnit(),
            yardUm: priceEntry.weightUnits || defaultYardUm,
            materialTypeId: priceEntry.materialTypeId,
            orderEntryId: priceEntry.id,
            materialName: formatMaterialName({
              commonName: priceEntry.materialName,
              code: priceEntry.materialCode,
              overrideName: priceEntry.overrideName,
            }),
          }
        })
      }

      // For materials without duplicates, add them to the map without the entry ID
      if (isInboundTicket(ticketType)) {
        ticketInfo.materials?.forEach(material => {
          const { materialTypeId, orderEntryId, weightAndPrice } = material
          if (weightAndPrice) {
            const key = `${materialTypeId}${orderEntryId ?? ''}${weightAndPrice.customerPreferredPrice?.amount ?? 0}`
            if (ocurrencesMap.get(materialTypeId) === 1) {
              priceDict[materialTypeId] = priceDict[key]
            }
          }
        })
      } else {
        priceSheetData.entriesObj.forEach(entry => {
          const key = `${entry.materialTypeId}${entry.id}`
          if (ocurrencesMap.get(entry.materialTypeId) === 1) {
            priceDict[entry.materialTypeId] = priceDict[key]
          }
        })
      }

      // add price, um, and materialName information to materials
      const materialsDictArray: any[] = Object.entries(materialsDict)
      materialsDictArray.sort((a, b) => a.materialName - b.materialName)
      const formattedMaterials = materialsDictArray.map(([key, material]) => {
        const priceInformation = priceDict[key]
        /**
         * TODO: we need to cover the case where there may be multiple materials with null order entry IDs on a ticket
         */

        // Add consumer info to the material
        const relevantTicket = invoiceData?.tickets?.find(({ id }) => id === ticketInfo.id)
        const obtMaterial = relevantTicket?.materials?.find(
          entry =>
            entry.materialTypeId === material.materialTypeId &&
            (material.orderEntryId ? entry.orderEntryId === material.orderEntryId : true),
        )

        // For inbound tickets, get adjustments from the original material data
        let adjustments: any[] = []
        if (isInboundTicket(ticketType)) {
          // Use the stored material ID for exact lookup
          const inboundMaterial = ticketInfo.materials?.find(m => m.id === material.materialId)
          adjustments = inboundMaterial?.weightAndPrice?.deductions || []
        } else {
          adjustments = obtMaterial?.consumerPriceWeight?.adjustments ?? []
        }

        // Either read from existing consumer info or use the yard info
        const formattedMaterial: any = {
          id: key,
          ...material,
          consumerMaterialName: obtMaterial?.consumerOverrideName ?? null,
          consumerItemCount: obtMaterial?.consumerPriceWeight?.itemCount ?? material?.yardItemCount,
          consumerGross: obtMaterial?.consumerPriceWeight?.grossWeight ?? material?.yardGross,
          consumerTare: obtMaterial?.consumerPriceWeight?.tareWeight ?? material?.yardTare,
          consumerPrice: obtMaterial?.consumerPriceWeight?.pricePerUnit ?? priceInformation?.yardPrice,
          consumerUm: obtMaterial?.consumerPriceWeight?.priceWeightUnit ?? priceInformation?.yardUm,
          deductions: adjustments,
        }

        if (priceInformation) {
          setValue(`consumerWeights.${ticketInfo.id}.${key}`, {
            materialTypeId: formattedMaterial.materialTypeId,
            orderEntryId: formattedMaterial.orderEntryId,
            consumerItemCount: formattedMaterial.consumerItemCount,
            consumerGross: formattedMaterial.consumerGross,
            consumerTare: formattedMaterial.consumerTare,
            consumerPrice: formattedMaterial.consumerPrice,
            consumerUm: formattedMaterial.consumerUm,
            consumerMaterialName: formattedMaterial.consumerMaterialName,
            deductions: formattedMaterial.deductions,
          })

          // For inbound tickets, prefer backend pre-calculated invoiceValue
          let value = 0
          if (isInboundTicket(ticketType)) {
            // Use the stored material ID for exact lookup
            const inboundMaterial = ticketInfo.materials?.find(m => m.id === material.materialId)

            // First try to use pre-calculated invoiceValue from backend
            if (inboundMaterial?.weightAndPrice?.invoiceValue !== undefined) {
              value = Dinero({ ...inboundMaterial.weightAndPrice.invoiceValue }).toUnit()
            } else if (inboundMaterial?.weightAndPrice?.netValue) {
              // Fallback to netValue without sign manipulation - backend should provide correct values
              value = Dinero({ ...inboundMaterial.weightAndPrice.netValue }).toUnit()
            }
          }

          return {
            ...formattedMaterial,
            ...priceInformation,
            // For inbound tickets, prioritize the material name from the ticket
            materialName:
              isInboundTicket(ticketType) && material.materialName
                ? material.materialName
                : priceInformation.materialName,
            value,
          }
        }
        const defaultMaterialType = materialTypesData.Items?.find(materialType => materialType.id === key)
        const defaultPriceInformation = {
          materialName:
            isInboundTicket(ticketType) && material.materialName
              ? material.materialName
              : defaultMaterialType
                ? `${defaultMaterialType?.commonName} (${defaultMaterialType?.code})`
                : '',
          yardUm: defaultMaterialType?.defaultPriceEntry?.weightUnits || defaultYardUm,
          consumerPrice: formattedMaterial.consumerPrice,
          consumerUm: formattedMaterial.consumerUm,
        }
        setValue(`consumerWeights.${ticketInfo.id}.${key}`, {
          materialTypeId: formattedMaterial.materialTypeId,
          orderEntryId: formattedMaterial.orderEntryId,
          consumerItemCount: formattedMaterial.consumerItemCount,
          consumerMaterialName: formattedMaterial.consumerMaterialName,
          consumerGross: formattedMaterial.consumerGross,
          consumerTare: formattedMaterial.consumerTare,
          consumerPrice: defaultPriceInformation.consumerPrice,
          consumerUm: defaultPriceInformation.consumerUm,
          deductions: formattedMaterial.deductions,
        })

        // For inbound tickets, prefer backend pre-calculated invoiceValue
        let value = 0
        if (isInboundTicket(ticketType)) {
          // Use the stored material ID for exact lookup
          const inboundMaterial = ticketInfo.materials?.find(m => m.id === material.materialId)

          // First try to use pre-calculated invoiceValue from backend
          if (inboundMaterial?.weightAndPrice?.invoiceValue !== undefined) {
            value = Dinero({ ...inboundMaterial.weightAndPrice.invoiceValue }).toUnit()
          } else if (inboundMaterial?.weightAndPrice?.netValue) {
            // Fallback to netValue without sign manipulation - backend should provide correct values
            value = Dinero({ ...inboundMaterial.weightAndPrice.netValue }).toUnit()
          }
        }

        return {
          ...formattedMaterial,
          ...defaultPriceInformation,
          value,
        }
      })

      /**
       * Add freight charge row if there's a freight cost
       */
      // Handle both IBT data structure (freightCost.chargeBack) and invoice response structure (freightChargeback)
      const freightChargeback = ticketInfo.freightCost?.chargeBack || ticketInfo.freightChargeback || 0

      if (freightChargeback) {
        formattedMaterials.push({
          id: 'freight',
          materialName: 'Freight Charge',
          consumerMaterialName: 'Freight Charge',
          materialTypeId: '',
          orderEntryId: '',
          yardGross: 0,
          yardTare: 0,
          yardNet: 0,
          consumerGross: 0,
          consumerTare: 0,
          yardItemCount: 0,
          consumerItemCount: 0,
          yardPrice: 0,
          consumerPrice: 0,
          yardUm: defaultYardUm,
          consumerUm: defaultYardUm,
          value: isInboundTicket(ticketType) ? Math.abs(freightChargeback) : freightChargeback,
          deductions: [],
          allMaterialsNetArray: [],
          looseMaterialsNetArray: [],
        })
      }

      /**
       * Add fees and promotions rows for inbound tickets
       */
      // Handle both IBT data structure (feesAndPromotions) and invoice response structure (feesOrPromotions)
      const feesOrPromotions =
        ticketInfo.feesAndPromotions || ticketInfo.feeOrPromotion || ticketInfo.feesOrPromotions || []

      if (isInboundTicket(ticketType) && feesOrPromotions.length > 0) {
        feesOrPromotions.forEach(feeOrPromotion => {
          if (ticketInfo.status?.value === 'PAID' && !feeOrPromotion.applied) {
            return
          }

          let finalValue = feeOrPromotion.calculatedValue || feeOrPromotion.amount || 0

          if (feeOrPromotion.type === FEE_OR_PROMOTION_TYPE.FEE) {
            finalValue = Math.abs(finalValue)
          } else if (feeOrPromotion.type === FEE_OR_PROMOTION_TYPE.PROMOTION) {
            finalValue = -Math.abs(finalValue)
          }

          formattedMaterials.push({
            id: `fee-${feeOrPromotion.settingId || feeOrPromotion.name.replace(/\s+/g, '-')}`,
            materialName: feeOrPromotion.name,
            consumerMaterialName: feeOrPromotion.name,
            materialTypeId: '',
            orderEntryId: '',
            yardGross: 0,
            yardTare: 0,
            yardNet: 0,
            consumerGross: 0,
            consumerTare: 0,
            yardItemCount: 0,
            consumerItemCount: 0,
            yardPrice: 0,
            consumerPrice: 0,
            yardUm: defaultYardUm,
            consumerUm: defaultYardUm,
            value: finalValue,
            deductions: [],
            allMaterialsNetArray: [],
            looseMaterialsNetArray: [],
          })
        })
      }

      setMaterials(formattedMaterials)
    }
  }, [invoiceData, ticketInfo, priceSheetData, materialTypesData, isEditMode, defaultYardUm, ticketType])

  const createTotalRow = () => {
    apiRef.current?.updateRows([{ id: 'total', code: 'Total' }])
    const rows = Array.from(apiRef.current?.getRowModels().values() || []).slice(0, -1)

    // Calculate basic totals
    const yardGross = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'yardGross' },
    )
    const yardTare = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'yardTare' },
    )
    const yardNet = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'yardNet' },
    )

    const yardItemCount = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'yardItemCount' },
    )
    const consumerGross = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'consumerGross' },
    )
    const consumerTare = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'consumerTare' },
    )
    const consumerNet = rows
      .filter(row => row.id !== 'freight' && !row.id.startsWith('fee-'))
      .reduce((acc, row) => {
        return acc + calculateRowNetWeight(row)
      }, 0)
    const consumerItemCount = calculateTotalWeight(
      rows.filter(row => row.id !== 'freight' && !row.id.startsWith('fee-')),
      { fieldName: 'consumerItemCount' },
    )

    let value = 0

    // Calculate total from individual row values (consistent with row-level logic)
    value = rows.reduce((aggregate, row) => {
      if (row.id === 'freight' || row.id.startsWith('fee-')) {
        return aggregate + (row.value || 0)
      }

      // For inbound tickets, use the pre-calculated value we set directly (now uses valueBeforeAdjustments)
      if (isInboundTicket(ticketType)) {
        if (row.value !== undefined) {
          return aggregate + row.value
        }
        // No pre-calculated value available
        return aggregate + 0
      }

      // For outbound tickets, continue with normal calculation
      const rowNetValue = calculateRowNetValue({ weightMode, row, enablePullApartFeatures, ticketType })
      return aggregate + rowNetValue
    }, 0)

    const roundedValue = roundTo(value, ROUNDING_PRECISIONS.COST)

    apiRef.current?.updateRows([
      {
        id: 'total',
        materialName: 'Total',
        yardItemCount,
        yardGross,
        yardTare,
        yardNet,
        consumerItemCount,
        consumerGross,
        consumerTare,
        consumerNet,
        value: roundedValue,
      },
    ])

    const freightChargeback = ticketInfo?.freightCost?.chargeBack || ticketInfo?.freightChargeback || 0

    updateTotalRowData(ticketInfo?.id, {
      grossWeight: weightMode === INVOICE_WEIGHT_MODES.YARD ? yardGross : consumerGross,
      tareWeight: weightMode === INVOICE_WEIGHT_MODES.YARD ? yardTare : consumerTare,
      grossValue: roundedValue,
      tareValue: 0,
      freightValue: isInboundTicket(ticketType) ? Math.abs(freightChargeback) : freightChargeback,
    })
  }

  useEffect(() => {
    createTotalRow()
  })

  const onFirstRowAdd = useCallback(() => {
    setTimeout(createTotalRow, 200)
  }, [apiRef])

  const materialColumns = useMemo(() => {
    const columns = [
      {
        field: 'materialName',
        headerName: 'Material Name',
        flex: 2,
        headerAlign: 'left',
        align: 'left',
        sortable: false,
        renderCell: params => {
          const { row = {} } = params,
            { materialName, consumerMaterialName } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? materialName : consumerMaterialName ?? ''
          if (row.id === 'total' || row.id === 'freight' || row.id.startsWith('fee-')) {
            return row.materialName
          }
          if (
            isEditMode &&
            ticketInfo?.invoiceStatuses?.[0]?.status !== 'PAID' &&
            weightMode == INVOICE_WEIGHT_MODES.CONSUMER &&
            (enableTransferTicketsConsumerWeightsFF ? !invoiceData?.lockConsumerWeights : true)
          ) {
            return (
              <EditableCell
                name={`consumerWeights.${ticketInfo.id}.${row.id}.consumerMaterialName`}
                control={control}
                placeholder={row.materialName}
                defaultValue={value || ''}
                isEdit
                size="small"
                InputProps={{
                  inputProps: {
                    onChange: event => {
                      apiRef.current?.updateRows([
                        {
                          id: row.id,
                          consumerMaterialName: event.target.value.trim() || null,
                        },
                      ])
                    },
                  },
                }}
              />
            )
          } else {
            return value || row.materialName
          }
        },
      },
      {
        field: 'itemCount',
        headerName: 'Count',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { id, yardItemCount = 0, consumerItemCount = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardItemCount : consumerItemCount
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return value
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerItemCount`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'consumerItemCount',
                  inputType: 'number',
                  decimalScale: 0,
                  allowNegative: false,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerItemCount: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'gross',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Gross`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { id, yardGross = 0, consumerGross = 0, consumerTare = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardGross : consumerGross
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${formatNumber(value)} ${defaultYardUm}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerGross`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'gross',
                  inputType: 'number',
                  decimalScale: 0,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerGross: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'tare',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Tare`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { id, yardTare = 0, consumerGross = 0, consumerTare = 0 } = row
          const value = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardTare : consumerTare
          if (
            !isEditMode ||
            id === 'total' ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${formatNumber(value)} ${defaultYardUm}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerTare`}
              control={control}
              defaultValue={value || value === 0 ? String(value) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'tare',
                  inputType: 'number',
                  decimalScale: 0,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerTare: event.target.value,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'adjustments',
        headerName: 'Adjustments',
        minWidth: 80,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        sortable: false,
        renderCell: params => {
          const {
            id,
            deductions = [],
            yardGross,
            yardTare,
            yardPrice,
            consumerGross,
            consumerTare,
            consumerPrice,
            consumerUm,
            yardUm,
          } = params.row

          if (id === 'total') {
            return '' // No total deductions display for now
          }

          if (id === 'freight' || id.startsWith('fee-')) {
            return ''
          }

          // For inbound tickets, use yard weights; for outbound tickets, use consumer weights
          const gross = isInboundTicket(ticketType) ? yardGross : consumerGross
          const tare = isInboundTicket(ticketType) ? yardTare : consumerTare
          const price = isInboundTicket(ticketType) ? yardPrice : consumerPrice
          const um = isInboundTicket(ticketType) ? yardUm : consumerUm

          const totalWeightDeduction = getTotalWeightDeduction(deductions, gross - tare)
          const totalPriceDeduction = getTotalPriceDeduction(
            deductions,
            convertUM((gross - tare) * price, defaultYardUm, um),
          )
          const deductionText = generateAdjustmentText(totalWeightDeduction, totalPriceDeduction, pricePrepend)

          // For inbound tickets, adjustments are not editable on invoices (only viewable)
          if (
            !isEditMode ||
            isInboundTicket(ticketType) ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return deductionText
          }

          // For editable outbound tickets, show button to open deduction modal
          return (
            <Box>
              <TransparentIconLabelButton
                endIcon={deductions.length ? <RemoveRedEyeIcon /> : null}
                text={deductions.length ? deductionText : 'Add'}
                variant="text"
                fullWidth
                onClick={() => openDeductionModal(id, deductions)}
                sx={{
                  justifyContent: 'flex-end',
                }}
              />
            </Box>
          )
        },
      },
      {
        field: 'net',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Net`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { yardNet = 0, consumerNet } = row

          let value = 0
          if (weightMode === INVOICE_WEIGHT_MODES.YARD) {
            value = yardNet
          } else {
            value = consumerNet ?? calculateRowNetWeight(row)
          }

          return `${formatNumber(value)} ${defaultYardUm}`
        },
        sortable: false,
      },
      {
        field: 'price',
        headerName: `${weightMode === INVOICE_WEIGHT_MODES.YARD ? 'Yard' : 'Consumer'} Price${disableUmDropdowns ? '/UM' : ''}`,
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        type: 'number',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { id, yardPrice = 0, consumerPrice = 0 } = row
          let price = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardPrice : consumerPrice
          if (isInboundTicket(ticketType) && !invoiceData) {
            // We need to flip the price when reading from an IBT
            // We provide a flipped total for the IBT in the response, but not for the price
            price = -price
          }

          if (id === 'total') return ''
          if (
            !isEditMode ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return `${pricePrepend}${setDecimalPrecision(price, { decimalPrecisionOverride: decimalPrecision })}`
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerPrice`}
              control={control}
              defaultValue={price || price === 0 ? String(price) : ''}
              isEdit
              size="small"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  name: 'price',
                  inputType: 'price',
                  pricePrepend,
                  decimalScale: decimalPrecision,
                  onFocus: event => setTimeout(() => event.target.focus(), 100),
                  onChange: event => {
                    const newPrice = event.target.value
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerPrice: newPrice,
                      },
                    ])
                    createTotalRow()
                  },
                  style: {
                    fontSize: '0.875rem',
                    textAlign: 'right',
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'slash',
        headerName: '/',
        headerAlign: 'center',
        align: 'center',
        width: 4,
        renderCell: ({ row }) =>
          row?.id === 'total' || row?.id === 'freight' || row?.id.startsWith('fee-') ? '' : '/',
        sortable: false,
      },
      {
        field: 'um',
        headerName: 'UM',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: params => {
          const { row = {} } = params
          if (row.id === 'freight' || row.id.startsWith('fee-')) return ''
          const { id, yardUm, consumerUm } = row
          const um = weightMode === INVOICE_WEIGHT_MODES.YARD ? yardUm : consumerUm
          if (id === 'total') return ''
          if (
            !isEditMode ||
            weightMode === INVOICE_WEIGHT_MODES.YARD ||
            invoiceData?.status === 'PAID' ||
            (enableTransferTicketsConsumerWeightsFF ? invoiceData?.lockConsumerWeights : false)
          ) {
            return um?.toUpperCase()
          }
          return (
            <EditableCell
              name={`consumerWeights.${ticketInfo.id}.${id}.consumerUm`}
              label=""
              control={control}
              defaultValue={um?.toLowerCase() || ''}
              cellType="SELECT"
              size="small"
              options={
                enablePullApartFeatures ? MEASURE_UNIT_ARRAY : MEASURE_UNIT_ARRAY.filter(unit => unit.id !== 'ea')
              }
              isEdit
              InputProps={{
                sx: { fontSize: '0.875rem' },
                inputProps: {
                  onChange: event => {
                    const newUm = event.target.value
                    apiRef.current?.updateRows([
                      {
                        id,
                        consumerUm: newUm,
                      },
                    ])
                    createTotalRow()
                  },
                },
              }}
            />
          )
        },
        sortable: false,
      },
      {
        field: 'value',
        headerName: 'Value',
        flex: 1,
        headerAlign: 'right',
        align: 'right',
        valueGetter: params => {
          const { value: rowValue, id } = params.row

          // For total, freight, or fee rows, use the calculated value directly
          if (id === 'total' || id === 'freight' || id.startsWith('fee-')) {
            return formatNumberForDisplay(rowValue, {
              format: 'price',
              pricePrepend,
              decimalPrecisionOverride: 2,
            })
          }

          // For inbound tickets, use the pre-calculated value we set directly
          if (isInboundTicket(ticketType)) {
            if (params.row.value !== undefined) {
              // Use the pre-calculated value (backend already applied all logic)
              return formatNumberForDisplay(params.row.value, {
                format: 'price',
                pricePrepend,
                decimalPrecisionOverride: 2,
              })
            } else {
              // No pre-calculated value available - show 0
              return formatNumberForDisplay(0, {
                format: 'price',
                pricePrepend,
                decimalPrecisionOverride: 2,
              })
            }
          }

          // For outbound tickets, always calculate dynamically in edit mode to reflect changes
          const value = isEditMode
            ? calculateRowNetValue({ weightMode, row: params.row, enablePullApartFeatures, ticketType })
            : rowValue || calculateRowNetValue({ weightMode, row: params.row, enablePullApartFeatures, ticketType })

          return formatNumberForDisplay(value, {
            format: 'price',
            pricePrepend,
            decimalPrecisionOverride: 2,
          })
        },
        sortable: false,
      },
    ]
    const filteredColumns = columns.filter(column => {
      if (disableUmDropdowns && (column.field === 'um' || column.field === 'slash')) return false
      if (!enablePullApartFeatures && column.field === 'itemCount') return false
      // Show adjustments for inbound tickets (which use yard weights) and for outbound tickets using consumer weights
      if (weightMode === INVOICE_WEIGHT_MODES.YARD && column.field === 'adjustments' && !isInboundTicket(ticketType))
        return false
      return true
    })
    return filteredColumns
  }, [
    pricePrepend,
    weightMode,
    isEditMode,
    invoiceData,
    defaultYardUm,
    ticketInfo,
    control,
    decimalPrecision,
    disableUmDropdowns,
    enablePullApartFeatures,
    ticketType,
  ])

  return (
    <>
      <Box>
        <Stack direction="row" alignItems="center" mt={4} mb={1}>
          {isInLockedAccountingPeriodV2 && (
            <Tooltip title={generateLockTooltip('outbound ticket')} placement="top">
              <LockIcon color="info" fontSize="small" sx={{ mr: 1 }} />
            </Tooltip>
          )}
          <Typography variant="subtitle1">
            <Link
              to={`/app/${isInboundTicket(ticketType) ? 'purchases/ticket-library' : 'sales/outbound-tickets'}/${ticketInfo.id}`}
              target="_blank"
            >
              {`Ticket #${ticketInfo?.ticketId || ticketInfo?.tag}`}
            </Link>
          </Typography>
          <Typography ml={1} variant="caption">
            {isInboundTicket(ticketType)
              ? startCase(toLower(ticketInfo?.status?.value || ticketInfo?.status || 'Unknown'))
              : startCase(toLower(ticketInfo?.status || ticketInfo?.jsonData?.status))}
          </Typography>
          {isOutboundTicket(ticketType) && (
            <>
              <Typography ml={5}>Mill Scale Ticket #: {ticketInfo?.millScaleNumber}</Typography>
              <Typography ml={5}>Booking Ref #: {ticketInfo?.contractInfo?.bookingRef}</Typography>
              <Typography ml={5}>Container #: {ticketInfo?.transportationInfo?.container}</Typography>
            </>
          )}
        </Stack>
        <GridStyling>
          <BasicDataGrid
            height="auto"
            apiRef={apiRef}
            loading={isLoading}
            rows={materials}
            columns={materialColumns}
            onCellClick={(_, event) => {
              event.stopPropagation()

              Object.values(apiRefDict).forEach((ref: any) => {
                ref.current.state.focus.cell = null
              })
            }}
            disableColumnMenu
            hideFooter
            getRowClassName={params => (params.row.id === 'total' ? 'total-row' : 'normal-row')}
            onMounted={onFirstRowAdd}
          />
        </GridStyling>
      </Box>
    </>
  )
}

export default InvoiceTicketTableV2
